import React, { useRef, useState } from 'react';
import { Button, Theme, TinyTitle, useFlashMessage } from '@indigo-ag/component-module';
import { AgFinancingDocumentType, AgFinancingFileFolder, AgFinancingFilesContext } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';

import { Card } from 'shared';
import i18next from 'core/i18n';
import { processFileUploadPatrimonyValuation } from 'features/brazil-grower/queries/financingProcess.queries';
import { uploadFilesToAgFinancing } from 'features/brazil-grower/components/BrazilGrowerFinance/utils/agFinancingFiles.util';
import { createStylesHook } from 'shared/hooks/createStylesHook';
import styled from 'styled-components';

export const useStyles = createStylesHook((theme: Theme) => ({
  noDisplay: {
    display: 'none'
  }
}));

const CardContainer = styled.div`
  height: 100%;
  flex: 1;
  min-width: 300px;
`;

export const PatrimonyUpload: React.FunctionComponent = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const [processFileUploadPatrimonyValuationFn] = useMutation(processFileUploadPatrimonyValuation);
  const { addFlashMessage } = useFlashMessage();
  const classes = useStyles();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const fileData: File[] = Array.from(event.target.files || []);
    notifyFilesChanges(fileData[0]);
    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.value = '';
  };

  const uploadFilesToCredit = uploadFilesToAgFinancing({
    folder: AgFinancingFileFolder.PATRIMONY,
    agFinancingId: ''
  });

  const notifyFilesChanges = (newFile: File) => {
    if (newFile) {
      setFile(newFile);
    } else {
      addFlashMessage({
        content: i18next.t('errorUploading'),
        title: '',
        variant: 'error'
      });
    }
  };

  const onChooseFile = (): void => {
    (fileInputRef as React.MutableRefObject<HTMLInputElement>).current.click();
  };

  const uploadFile = async () => {
    if (file) {
      const filesUploaded = await uploadFilesToCredit(
        [file],
        AgFinancingFilesContext.PATRIMONY,
        AgFinancingDocumentType.OTHER
      );
      const filesUploadedWithAllInfo = filesUploaded.fulfilled.map(file => ({
        ...file
      }));
      processFileUploadPatrimonyValuationFn({
        variables: {
          file: {
            documentName: filesUploadedWithAllInfo[0].documentName,
            documentUrl: filesUploadedWithAllInfo[0].documentUrl
          }
        }
      })
        .then(({ data }) => {
          if (data.processFileUploadPatrimonyValuation) {
            addFlashMessage({
              content: i18next.t('success'),
              title: '',
              variant: 'success'
            });
            setFile(undefined);
          }
        })
        .catch(() => {
          addFlashMessage({
            content: i18next.t('error'),
            title: '',
            variant: 'error'
          });
        });

      return;
    } else {
      return Promise.reject();
    }
  };

  const disableUpload = !file;

  return (
    <CardContainer>
      <Card
        title={
          <TinyTitle>
            {i18next.t('patrimonyValuationUpload')} {file?.name}
          </TinyTitle>
        }
      >
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className={classes.noDisplay} />

        <Button onClick={onChooseFile} size="small" variant="tertiary">
          {i18next.t('uploadCSV')}
        </Button>

        <Button size="small" className="text-transform-capitalize" disabled={disableUpload} onClick={uploadFile}>
          {i18next.t('submit')}
        </Button>
      </Card>
    </CardContainer>
  );
};
